import React, { useState, useEffect } from 'react';
import { useI18n } from '@/contexts/I18nContext';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SossietyDialogHeader, SossietyDialogClose } from '@/components/ui/SossietyDialogHeader';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { Training } from '@/types/trainings';
import { StudentDisplay } from '@/components/ui/StudentDisplay';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';

// Categorias de técnicas baseadas no padrão do projeto
const techniqueCategories = [
  {
    name: 'guard_types',
    techniques: [
      'Closed Guard',
      'Open Guard (e.g., De La Riva, Spider, Lasso, X-Guard, Half Guard)',
      'Half Guard (e.g., Deep Half, Z-Guard, Knee Shield)',
      'Butterfly Guard',
      'Worm Guard',
      'Reverse De La Riva Guard (RDLR)',
      '50/50 Guard',
      'Sitting Guard',
      'No-Gi Specific Guards (e.g., Leg Pummeling, Ashi Garami)',
      'Other Guard'
    ],
  },
  {
    name: 'guard_passing',
    techniques: [
      'Pressure Passing (e.g., Knee Slice, Over Under, Stack Pass)',
      'Torque/Leg Drag Passing',
      'Leg Weave Passing',
      'Floating/Smash Passing',
      'Movement-Based Passing (e.g., Toreando, Matador)',
      'Folding Passes (e.g., Double Under)',
      'No-Gi Specific Passes (e.g., Head & Arm Pass, Darce Pass from Guard)',
      'Other Guard Pass'
    ],
  },
  {
    name: 'submissions',
    techniques: [
      'Armlocks (e.g., Armbar, Kimura, Americana, Omoplata)',
      'Chokes (e.g., Rear Naked Choke, Guillotine, Triangle, Cross Collar Choke, Darce, Anaconda, Bow and Arrow)',
      'Leg Locks (e.g., Heel Hook, Kneebar, Ankle Lock, Calf Slicer)',
      'Shoulder Locks (e.g., Wrist Lock, Omoplata, Americana)',
      'Spine/Neck Cranks (e.g., Twister)',
      'No-Gi Specific Submissions (e.g., Head & Arm Choke, Ezekiel from Mount)',
      'Other Submission'
    ],
  },
  {
    name: 'sweeps_takedowns',
    techniques: [
      'Guard Sweeps (e.g., Scissor Sweep, Hip Bump Sweep, Flower Sweep, Hook Sweeps)',
      'Half Guard Sweeps (e.g., Old School Sweep, Waiter Sweep)',
      'Butterfly Sweeps',
      'De La Riva Sweeps',
      'Takedowns from Standing (e.g., Double Leg, Single Leg, Judo Throws, Body Lock)',
      'Counter Takedowns',
      'Pulling Guard',
      'Other Sweep / Takedown'
    ],
  },
  {
    name: 'escapes_reversals',
    techniques: [
      'Mount Escapes (e.g., Upa, Elbow-Knee Escape)',
      'Back Escapes (e.g., Bridge & Roll, Hip Escape)',
      'Side Control Escapes (e.g., Hip Escape to Guard, Underhook Escapes)',
      'North South Escapes',
      'Submission Escapes (e.g., Armbar Escape, Triangle Escape)',
      'Turtle Escapes',
      'Half Guard Escapes',
      'Other Escape / Reversal'
    ],
  },
];

interface ClassNotesModalProps {
  isOpen: boolean;
  onClose: () => void;
  classData: Training | null;
  notesData?: any;
  onSave?: (classId: string, notes: string) => void;
  onDataSaved?: () => void;
}

export const ClassNotesModal = ({
  isOpen,
  onClose,
  classData,
  notesData: propNotesData,
  onSave,
  onDataSaved
}: ClassNotesModalProps) => {
  const { toast } = useToast();
  const { t, currentLanguage } = useI18n();
  const [fullNotes, setFullNotes] = useState('');
  const [shareWithCommunity, setShareWithCommunity] = useState(false);
  const [privacyLevel, setPrivacyLevel] = useState<'public' | 'connections' | 'gym'>('public');
  const [students, setStudents] = useState<any[]>([]);
  const [isLoadingStudents, setIsLoadingStudents] = useState(false);
  const [selectedTechniques, setSelectedTechniques] = useState<string[]>([]);
  const [showTechniques, setShowTechniques] = useState(false);
  const [openCategories, setOpenCategories] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [forceClose, setForceClose] = useState(false);
  const queryClient = useQueryClient();

  // Get class notes data from props
  const classNotesData = propNotesData?.[classData?.id || ''];
  const data = classNotesData;

  // Fetch students when modal opens
  useEffect(() => {
    if (isOpen && classData?.id && classData.type === 'Private Class') {
      fetchStudents();
    }
  }, [isOpen, classData?.id, classData?.type]);

  const fetchStudents = async () => {
    if (!classData?.id) return;
    
    setIsLoadingStudents(true);
    try {
      const { data, error } = await supabase
        .from('training_partners')
        .select(`
          partner_user_id,
          profiles!inner(
            id,
            full_name,
            username,
            profile_picture_url,
            belt_rank
          )
        `)
        .eq('training_id', classData.id);

      if (error) {
        console.error('Error fetching students:', error);
        return;
      }

      setStudents(data || []);
    } catch (error) {
      console.error('Error fetching students:', error);
    } finally {
      setIsLoadingStudents(false);
    }
  };

  // Function to save notes using Edge Function
  const saveNotes = async (notesData: any) => {
    if (!classData?.id) throw new Error('No class ID');
    
    const { data: { session } } = await import('@/integrations/supabase/client').then(m => m.supabase.auth.getSession());
    const token = session?.access_token;

    if (!token) throw new Error('No access token');

    const baseUrl = import.meta.env.DEV
      ? 'http://127.0.0.1:54321'
      : import.meta.env.VITE_SUPABASE_URL;

    const response = await fetch(`${baseUrl}/functions/v1/save_training_notes`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({
        training_id: classData.id,
        ...notesData
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to save notes');
    }

    const result = await response.json();
    return result;
  };

  useEffect(() => {
    if (data?.notes) {
      setFullNotes(data.notes.full_notes || '');
      setShareWithCommunity(data.notes.shared_with_community);
      setPrivacyLevel(data.notes.privacy_level || 'public');
    }
    if (data?.techniques) {
      // Convert technique names back to unique IDs for display
      const techniqueIds = data.techniques
        .map(technique => {
          // Find which category and index this technique belongs to
          for (const category of techniqueCategories) {
            const index = category.techniques.findIndex(t => t === technique.name);
            if (index !== -1) {
              return `${category.name}_${index + 1}`;
            }
          }
          return null;
        })
        .filter(Boolean);
      setSelectedTechniques(techniqueIds);
      
      // If there are techniques, keep the techniques section closed by default
      if (techniqueIds.length > 0) {
        setShowTechniques(false); // Keep closed by default
        setOpenCategories([]); // Close all categories by default
      }
    }
  }, [data]);

  // Force close effect
  useEffect(() => {
    if (forceClose) {
      onClose();
      setForceClose(false);
    }
  }, [forceClose, onClose]);

  const handleTechniqueSelect = (technique: string) => {
    setSelectedTechniques(prev => {
      if (prev.includes(technique)) {
        return prev.filter(t => t !== technique);
      } else {
        return [...prev, technique];
      }
    });
  };

  const toggleCategory = (categoryName: string) => {
    setOpenCategories(prev => {
      if (prev.includes(categoryName)) {
        return prev.filter(cat => cat !== categoryName);
      } else {
        return [...prev, categoryName];
      }
    });
  };

  const isCategoryOpen = (categoryName: string) => {
    return openCategories.includes(categoryName);
  };

  const handleShowTechniques = () => {
    setShowTechniques(!showTechniques);
    // Scroll to bottom after a short delay to allow content to render
    setTimeout(() => {
      const modalContent = document.querySelector('[data-radix-scroll-area-viewport]');
      if (modalContent) {
        modalContent.scrollTo({
          top: modalContent.scrollHeight,
          behavior: 'smooth'
        });
      }
    }, 100);
  };

  const handleSave = async () => {
    if (!classData) return;

    setSaving(true);
    try {
      // Convert technique IDs to technique names
      const techniqueNames = selectedTechniques.map(techId => {
        const lastUnderscoreIndex = techId.lastIndexOf('_');
        const categoryName = techId.substring(0, lastUnderscoreIndex);
        const indexStr = techId.substring(lastUnderscoreIndex + 1);
        const index = parseInt(indexStr) - 1;
        
        const category = techniqueCategories.find(cat => cat.name === categoryName);
        if (category && category.techniques[index]) {
          return category.techniques[index];
        }
        return techId; // fallback to original ID
      });



      // Save notes
      await saveNotes({
        general_notes: '', // Not used in this implementation
        full_notes: fullNotes,
        shared_with_community: shareWithCommunity,
        privacy_level: privacyLevel,
        technique_ids: techniqueNames
      });

      // Notify parent component that data was saved
      if (onDataSaved) {
        onDataSaved();
      }

      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['classes-with-notes'] });
      queryClient.invalidateQueries({ queryKey: ['instructor-classes'] });
      queryClient.invalidateQueries({ queryKey: ['journey-highlights'] });
      
      // Force refetch of the specific queries
      queryClient.refetchQueries({ queryKey: ['classes-with-notes'] });
      queryClient.refetchQueries({ queryKey: ['instructor-classes'] });
      
      // Also invalidate the specific date query
      queryClient.invalidateQueries({ 
        queryKey: ['instructor-classes', classData.user_id, format(new Date(classData.start_time), 'yyyy-MM-dd')] 
      });

      // Show success toast
      toast({
        title: "Notes saved successfully!",
        description: "Your class notes have been saved.",
      });

      // Force close the modal
      setForceClose(true);
    } catch (error) {
      console.error('Error saving notes:', error);
      toast({
        title: "Error saving notes",
        description: "Please try again.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setFullNotes('');
    setShareWithCommunity(false);
    setPrivacyLevel('public');
    setSelectedTechniques([]);
    setShowTechniques(false);
    setOpenCategories([]);
    onClose();
  };

  if (!classData) return null;

  const hasNotes = !!data?.notes;

  // Build class info string without empty values
  const classInfoParts = [
    classData.type || classData.title,
    classData.category,
    classData.modality
  ].filter(Boolean); // Remove empty/undefined values

  const classInfoString = classInfoParts.join(' - ');

  // Format date according to language
  const formatLocalizedDate = (date: Date) => {
    if (currentLanguage === 'pt') {
      // Portuguese format: "24 de agosto de 2025"
      const month = date.toLocaleDateString('pt-BR', { month: 'long' });
      const day = date.getDate();
      const year = date.getFullYear();
      return `${day} de ${month} de ${year}`;
    } else {
      // English format: "August 24, 2025"
      return format(date, 'MMMM d, yyyy');
    }
  };

  // Format time according to language
  const formatLocalizedTime = (date: Date) => {
    if (currentLanguage === 'pt') {
      // Portuguese format: "14:00 às 15:00"
      return format(date, 'HH:mm');
    } else {
      // English format: "2:00 PM"
      return format(date, 'h:mm a');
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="w-[90vw] max-w-2xl bg-white rounded-2xl p-6" aria-describedby="class-notes-description">
        <DialogDescription id="class-notes-description" className="sr-only">
          {hasNotes ? 'Edit your class notes' : 'Add notes for your class'}
        </DialogDescription>
        <SossietyDialogHeader 
          title={hasNotes ? t('calendar.edit_notes') : t('calendar.add_notes')}
          titleClassName="text-lg"
        >
          <SossietyDialogClose onClick={handleClose} />
        </SossietyDialogHeader>
        
        <div className="space-y-4 max-h-[75vh] overflow-y-auto pr-2">
          {/* Class Info - Type, Category, Modality */}
          <div className="rounded-lg">
            <h3 className="text-black font-sans text-sm font-semibold mb-1">
              {classInfoString}
            </h3>
          </div>

          {/* Date */}
          <div className="rounded-lg !mt-0">
            <p className="text-gray-500 font-sans text-xs">
              {formatLocalizedDate(new Date(classData.start_time))} - {formatLocalizedTime(new Date(classData.start_time))} {currentLanguage === 'pt' ? 'às' : 'to'} {formatLocalizedTime(new Date(classData.end_time))}
            </p>
          </div>

          {/* Link Techniques */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-semibold text-black">{t('calendar.link_techniques')}</h4>
              <button
                type="button"
                onClick={handleShowTechniques}
                className="text-xs text-[#D72E16] hover:text-[#B8251A] font-medium underline"
              >
                {showTechniques ? t('calendar.hide_techniques') : t('calendar.show_techniques')}
              </button>
            </div>
            {!showTechniques && selectedTechniques.length === 0 && (
              <div className="text-center mt-3 mb-3">
                <p className="text-xs font-medium text-gray-600">
                  {t('calendar.register_techniques_used')}
                </p>
                <p className="text-xs text-gray-600">
                  {t('calendar.techniques_filter_description')}
                </p>
              </div>
            )}

            {selectedTechniques.length > 0 && (
              <div className="mb-3">
                <div className="text-xs text-gray-600 mb-2">
                  Selected: {selectedTechniques.length} technique{selectedTechniques.length !== 1 ? 's' : ''}
                </div>
                <div className="flex flex-wrap gap-1">
                  {selectedTechniques.map((techId) => {
                    // Find the technique name by unique ID (category_index format)
                    let techniqueName = techId;
                    const lastUnderscoreIndex = techId.lastIndexOf('_');
                    const categoryName = techId.substring(0, lastUnderscoreIndex);
                    const indexStr = techId.substring(lastUnderscoreIndex + 1);
                    const index = parseInt(indexStr) - 1;
                    
                    const category = techniqueCategories.find(cat => cat.name === categoryName);
                    if (category && category.techniques[index]) {
                      techniqueName = category.techniques[index];
                    }
                    
                    // Use the same translation key pattern as the collapsible buttons
                    return (
                      <span
                        key={techId}
                        className="px-2 py-1 bg-[#D72E16] text-white rounded-full text-xs font-medium"
                      >
                        {techniqueName}
                      </span>
                    );
                  })}
                </div>
              </div>
            )}

            {showTechniques && (
              <div className="space-y-3 mt-4">
                {techniqueCategories.map((category) => (
                  <Collapsible 
                    key={category.name} 
                    open={isCategoryOpen(category.name)} 
                    onOpenChange={() => toggleCategory(category.name)}
                  >
                    <CollapsibleTrigger className="flex items-center justify-between w-full px-3 py-2 text-sm font-medium text-left text-black bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">
                      <span>{t(`calendar.techniques.${category.name}`)}</span>
                      {isCategoryOpen(category.name) ? (
                        <ChevronUp className="h-4 w-4 text-gray-500" />
                      ) : (
                        <ChevronDown className="h-4 w-4 text-gray-500" />
                      )}
                    </CollapsibleTrigger>
                    <CollapsibleContent className="pt-2">
                      <div className="flex flex-wrap gap-1">
                        {category.techniques.map((technique, index) => {
                          // Create unique technique ID by combining category and index
                          const techniqueId = `${category.name}_${index + 1}`;
                          const isSelected = selectedTechniques.includes(techniqueId);
                          return (
                            <button
                              key={technique}
                              type="button"
                              onClick={() => handleTechniqueSelect(techniqueId)}
                              className={`px-2 py-1 text-xs font-medium rounded border transition-colors duration-200 ${
                                isSelected
                                  ? 'bg-[#D72E16] text-white border-[#D72E16]'
                                  : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
                              }`}
                            >
                              {technique}
                            </button>
                          );
                        })}
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                ))}
              </div>
            )}
          </div>

          {/* Students - Only for Private Classes */}
          {classData?.type === 'Private Class' && (
            <div className="space-y-3">
              <label className="block text-sm font-medium text-black">
                {t('calendar.students')}
              </label>
              
              {/* Selected Students List */}
              <div className="space-y-2">
                {isLoadingStudents ? (
                  <p className="text-xs text-gray-500">Loading students...</p>
                ) : students.length === 0 ? (
                  <p className="text-xs text-gray-500">No students found for this class.</p>
                ) : (
                  students.map((student) => (
                    <StudentDisplay
                      key={student.partner_user_id}
                      student={student.profiles}
                      showRemoveButton={false}
                    />
                  ))
                )}
              </div>
            </div>
          )}

          {/* Full Class Notes with Share with Community */}
          <div>
            <h3 className="block text-sm font-semibold text-black mb-2">
              {t('calendar.full_class_notes')}
            </h3>
            <div className="text-xs text-gray-500 mb-2">
              ({fullNotes.length}/2000)
            </div>
            <textarea
              className="w-full h-32 p-3 border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              style={{ fontSize: '14px' }}
              value={fullNotes}
              onChange={(e) => setFullNotes(e.target.value.slice(0, 2000))}
              placeholder={t('calendar.detailed_reflections')}
            />
          </div>

          {/* Share with Community */}
          <div className="mb-4">
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={shareWithCommunity}
                onChange={(e) => setShareWithCommunity(e.target.checked)}
                className="rounded"
              />
              <span className="text-sm text-black">{t('calendar.share_with_community')}</span>
            </label>

            {shareWithCommunity && (
              <div className="!mt-5">
                <label className="block text-xs text-black">{t('calendar.who_can_see_post')}</label>
                <div className="w-full">
                  <Select value={privacyLevel} onValueChange={(value: 'public' | 'connections' | 'gym') => setPrivacyLevel(value)}>
                    <SelectTrigger className="w-full h-8 text-xs text-black relative [&_[data-radix-select-icon]]:hidden">
                      <SelectValue />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                        <ChevronDown className="w-4 h-4 text-black" />
                      </span>
                    </SelectTrigger>
                    <SelectContent className="w-[var(--radix-select-trigger-width)] max-w-[var(--radix-select-trigger-width)] bg-black border-0 py-2 space-y-1">
                      <SelectItem value="public" className="text-white truncate">{t('social.whole_community')}</SelectItem>
                      <SelectItem value="connections" className="text-white truncate">{t('calendar.only_my_connections')}</SelectItem>
                      <SelectItem value="gym" className="text-white truncate">{t('calendar.only_my_gym')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="mt-6 pt-4 border-t border-gray-200">
          <Button
            className="w-full bg-[#D72E16] hover:bg-[#B8251A] text-white font-medium py-2.5 h-10 rounded-full transition-colors duration-200 disabled:opacity-50"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? t('calendar.saving') : (hasNotes ? t('calendar.update_notes') : t('calendar.save_notes'))}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
